import { paths } from '@/routes/paths';

/**
 * Where a notification points.
 *
 * ORGANIZATION_SUBMITTED is what lands an application in the reviewer's
 * queue, so it opens the application review page — the organization, its
 * licences and the acts on one screen. LICENSE_SUBMITTED fires for a licence
 * added to an organization that is already active (a renewal), so it opens
 * that organization's licences; it carries the licence as its entity and the
 * organization in `organizationId`. The licence-expiry pair belongs to the
 * tenant's own organization page.
 */
export function notificationTarget(item: {
  type?: string;
  entityType?: string;
  entityId?: string;
  organizationId?: string;
}): string | null {
  switch (item.type) {
    case 'ORGANIZATION_SUBMITTED':
      return item.entityId ? paths.application(item.entityId) : paths.applications;
    case 'LICENSE_SUBMITTED':
      return item.organizationId
        ? `${paths.organization(item.organizationId)}?tab=licenses`
        : `${paths.licenses}?reviewStatus=PENDING`;
    /*
     * An import failure carries the run as its entity, and a run has no page
     * of its own; what the administrator needs is the freshness of all four
     * sources and the log, which is one screen (2026-10-04).
     */
    case 'REGISTRY_IMPORT_FAILED':
      return paths.registerImports;
    /*
     * An extraction has no page of its own, and it should not: the only thing
     * anyone wants with a read invoice is to put its lines into the delivery
     * they uploaded it for. So the notification lands on the request form with
     * the job in the query string, which opens it straight onto the lines.
     * Without an id there is nothing to open — the form's own list of recent
     * invoices is then the way in.
     *
     * `_PARTIAL` (2026-10-19) is sent instead of `_READY` when a field was
     * given up on, or when there were no line items at all. It lands in the
     * same place for the same reason: the lines are there and usable, and the
     * dialog is where the fields that could not be read are named. A read
     * announced as partial that pointed nowhere would be the original bug in
     * a politer voice.
     */
    case 'INVOICE_EXTRACTION_READY':
    case 'INVOICE_EXTRACTION_PARTIAL':
    case 'INVOICE_EXTRACTION_FAILED':
      return item.entityId
        ? `${paths.deliveryNew}?extraction=${item.entityId}`
        : paths.deliveryNew;
    /*
     * The platform's reading allowance, to the people who can raise it. There
     * is nothing on any screen to act on, so it points at nothing rather than
     * at a page that would only shrug.
     */
    case 'INVOICE_EXTRACTION_QUOTA_EXHAUSTED':
      return null;
    /*
     * The live tracking feed went down, or came back (2026-10-20). Both are
     * about every vehicle at once, so they open the map, whose banner says
     * since when and why — not a vehicle and not the units list.
     */
    case 'TRACKING_FEED_DOWN':
    case 'TRACKING_FEED_RESTORED':
      return paths.fleetMap;
    case 'LICENSE_EXPIRING':
    case 'LICENSE_EXPIRED':
      return paths.myOrganization;
    // Every delivery event carries the delivery as its entity; the detail
    // page shows the timeline the notification is one step of.
    case 'DELIVERY_REQUESTED':
    case 'DELIVERY_CREATED_FOR_YOU':
    case 'DELIVERY_ASSIGNED':
    case 'DELIVERY_IN_TRANSIT':
    case 'DELIVERY_DELIVERED':
    case 'DELIVERY_FAILED':
    case 'DELIVERY_CANCELLED':
      return item.entityId ? paths.delivery(item.entityId) : paths.deliveries;
    default:
      return null;
  }
}
